import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ClientApplication,
  ClientApplicationsResponse,
  ClientApplicationsResponseSchema,
} from '../../models/applications.model';
import { ApplicantContextStore } from '../../stores/applicant-context/applicant-context.store';
import { ApplicationsStore } from '../../stores/applications/applications.store';
import { ClientStore } from '../../stores/client/client.store';
import { readSelectedCase, takePendingCase } from '../../utils/case-link/case-link.util';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationsService extends SmartrService {
  private readonly http = inject(HttpClient);
  private readonly applicationsStore = inject(ApplicationsStore);
  private readonly applicantContextStore = inject(ApplicantContextStore);
  private readonly clientStore = inject(ClientStore);

  /**
   * Gets applications for a specific client
   * @param clientId - The client ID to get applications for
   * @returns Observable emitting the list of applications
   */
  get(clientId: number): Observable<ClientApplication[]> {
    return this.http.get<ClientApplicationsResponse>(this.getApplicationsUrl(clientId)).pipe(
      this.verifyResponse(ClientApplicationsResponseSchema),
      tap((response: ClientApplicationsResponse) => {
        const userId = response.Applications?.[0]?.Clients?.find(c => c.ClientId === clientId)?.UserId;
        
        if (userId) {
          this.clientStore.setUserId(userId);
        }
      }),
      map((response: ClientApplicationsResponse) => response.Applications),
      tap((applications: ClientApplication[]) => {
        if (applications?.length) {
          this.applicationsStore.setApplications(clientId, applications);
        } else {
          this.applicationsStore.setApplications(clientId, []);
        }

        this.applyLinkedOrRememberedCase(clientId, applications ?? []);
        this.applicantContextStore.sync(clientId, this.applicationsStore.factFindApplicants());
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  private applyLinkedOrRememberedCase(clientId: number, applications: ClientApplication[]): void {
    const caseId = takePendingCase() ?? readSelectedCase(clientId);
    if (caseId !== null && applications.some((app) => app.ApplicationId === caseId)) {
      this.applicationsStore.setSelectedApplication(caseId);
    }
  }

  private getApplicationsUrl(clientId: number): string {
    return environment.applications.get.replace('{{clientId}}', clientId.toString());
  }
}
